import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import deleteIcon from "../../assets/images/icons/delete-icon.svg";
import plusIcon from "../../assets/images/icons/plus_icon.svg";
import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import ClubbieInput from "../../components/ClubbieInput/ClubbieInput";
import { updateTournament } from "../../redux/slices/tournamentSlice";
import { DASHBOARD_STACK } from "../../utils/screenNames";

import "./ChooseTournament.css";

const CreateTournamentGroups = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedTournament = useSelector(
    (state) => state.tournament.selectedTournament
  );
  const [isLoading, setIsLoading] = useState(false);
  const [groups, setGroups] = useState([{ name: "" }]);
  const [errors, setErrors] = useState({});

  // Add refs for scrolling and focusing
  const groupsContainerRef = useRef(null);
  const lastInputRef = useRef(null);

  useEffect(() => {
    if (selectedTournament?.groups) {
      setGroups(selectedTournament.groups);
    }
  }, [selectedTournament]);

  // Add effect for scrolling and focusing when groups change
  useEffect(() => {
    if (groups.length > 1) {
      // Scroll to the bottom of the container
      if (groupsContainerRef.current) {
        const container = groupsContainerRef.current;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }

      // Focus on the last added input
      if (lastInputRef.current) {
        lastInputRef.current.focus();
      }
    }
  }, [groups.length]); // Only run when the number of groups changes

  const validateGroups = () => {
    const newErrors = {};
    let isValid = true;

    groups.forEach((group, index) => {
      if (!group.name.trim()) {
        newErrors[`group${index}Name`] = "Group name is required";
        isValid = false;
      } else if (
        groups.some(
          (g, i) =>
            i !== index && g.name.toLowerCase() === group.name.toLowerCase()
        )
      ) {
        newErrors[`group${index}Name`] = "Group name already exists";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = async () => {
    try {
      setIsLoading(true);

      if (!validateGroups()) {
        setIsLoading(false);
        return;
      }

      const formattedGroups = groups.map((group, index) => ({
        id: group.id || `group-${Date.now()}-${index}`,
        name: group.name.trim(),
      }));

      const result = await dispatch(
        updateTournament({
          id: selectedTournament?.id,
          updateData: {
            groups: formattedGroups,
          },
        })
      ).unwrap();

      if (result) {
        toast.success("Groups created successfully!");
        navigate(DASHBOARD_STACK.HOME);
      } else {
        throw new Error("Failed to create groups");
      }
    } catch (error) {
      toast.error(error.message || "Failed to create groups");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGroupChange = (index, value) => {
    const newGroups = [...groups];
    newGroups[index] = {
      ...newGroups[index],
      name: value,
    };
    setGroups(newGroups);
  };

  const handleDeleteGroup = (index) => {
    if (groups.length > 1) {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      setGroups(newGroups);

      // Clear any errors for this group
      const newErrors = { ...errors };
      delete newErrors[`group${index}Name`];
      setErrors(newErrors);
    } else {
      toast.error("You need at least one group");
    }
  };

  const handleAddGroup = () => {
    setGroups([...groups, { name: "" }]);
  };

  return (
    <>
      <div className="input-section">
        <p>Create Groups for your Tournament</p>

        <div className="groups-container-wrapper">
          <div className="groups-container" ref={groupsContainerRef}>
            {groups.map((group, index) => (
              <div key={index} className="my-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="mb-0 fw-bold">Group {index + 1}</label>
                  {groups.length > 1 && (
                    <img
                      src={deleteIcon}
                      alt="Delete Group"
                      className="trash-icon cursor-pointer"
                      onClick={() => handleDeleteGroup(index)}
                    />
                  )}
                </div>
                <div className="w-100">
                  <ClubbieInput
                    type="text"
                    value={group.name}
                    onChange={(e) => handleGroupChange(index, e.target.value)}
                    placeholder={`Enter a name for Group ${index + 1}`}
                    className={errors[`group${index}Name`] ? "error" : ""}
                    innerRef={index === groups.length - 1 ? lastInputRef : null}
                  />
                  {errors[`group${index}Name`] && (
                    <div className="error-message mt-2">
                      {errors[`group${index}Name`]}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <span
          onClick={handleAddGroup}
          className="add-group-button"
          type="button"
        >
          <img src={plusIcon} alt="Add Group" className="plus-icon-small m-2" />
          <span>Add New Group</span>
        </span>

        <ClubbieButton
          onClick={handleNext}
          disabled={isLoading || groups.length === 0}
          isLoading={isLoading}
        >
          Next
        </ClubbieButton>
      </div>
    </>
  );
};

export default CreateTournamentGroups;
